import { Button } from '@mui/material'
import { FC, useCallback, useState } from 'react'
import { InformationModal } from 'utoniq-ui'

import {
  MembershipSubscriptionPlanDetailFragment,
  MembershipSubscriptionPlanStatus,
} from '@/clients/utoniq-core/schema'
import {
  PatronDetailFragment,
  PaymentMethod,
} from '@/clients/utoniq-core/schema'
import { useMessage, useRouter, useTranslation } from '@/core'
import { captureError } from '@/core/errors'
import { isCarrierPaymentMethod } from '@/lib/escott'

import { Action } from './../'

type ResumePlanButtonProps = {
  membership: PatronDetailFragment
  resumingSubscription: boolean
  currentPlan?: MembershipSubscriptionPlanDetailFragment
  onResumeSubscription: Action['onResumeSubscription']
}

export const ResumePlanButton: FC<ResumePlanButtonProps> = ({
  membership,
  resumingSubscription,
  currentPlan,
  onResumeSubscription,
}) => {
  const { t } = useTranslation('membership')
  const { generatePath, routerPages } = useRouter()
  const { showError } = useMessage()
  const [openConfirm, setOpenConfirm] = useState<boolean>(false)

  const handleResumeSubscription = useCallback(async () => {
    return onResumeSubscription(
      membership.artist.id,
      membership.paymentMethod ?? PaymentMethod.CreditCard,
      {
        // 外部キャリア決済画面での処理が成功した場合のリダイレクト先
        successUrl: `${window.location.origin}${generatePath(
          routerPages.MembershipManagement
        )}`,
        // 外部キャリア決済画面での処理がキャンセルされた場合のリダイレクト先
        cancelUrl: `${window.location.origin}${generatePath(
          routerPages.MembershipManagement
        )}`,
        // 外部キャリア決済画面での処理がエラーになった場合のリダイレクト先
        errorUrl: `${window.location.origin}${generatePath(
          routerPages.MembershipManagement
        )}`,
      }
    )
      .catch(e => {
        captureError(e, 'MembershipTemplate')
        showError(new Error(t('error')))
      })
      .finally(() => {
        // mutation の完了から外部キャリア決済画面に遷移するまでの間に
        // 画面内で操作が行われてしまうことを防ぐため
        // キャリア決済の場合はモーダルを閉じない
        if (!isCarrierPaymentMethod(membership.paymentMethod))
          setOpenConfirm(false)
      })
  }, [membership, onResumeSubscription, showError, t])

  // ステータスの詳細は以下の通り
  // active: 有効
  // closed: 非公開（既存メンバーシップ会員は継続可能）
  // stopped: 停止（既存メンバーシップ会員は翌月自動解約）
  if (
    (membership.type !== 'stripe' && membership.type !== 'escott') ||
    !currentPlan ||
    currentPlan.status === MembershipSubscriptionPlanStatus.Stopped
  ) {
    return null
  }

  return (
    <>
      <Button
        size="large"
        variant="contained"
        color="inherit"
        sx={{
          width: 240,
          '&.MuiButtonBase-root:hover': { bgcolor: 'grey.50' },
          bgcolor: 'grey.50',
        }}
        onClick={() => setOpenConfirm(true)}
      >
        {t('resumeUpdate')}
      </Button>

      <InformationModal
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title={t('confirm')}
        content={t('resumeConfirmContent')}
        cancelText={t('cancel')}
        buttons={
          <Button
            size="large"
            sx={{ minWidth: 212 }}
            onClick={handleResumeSubscription}
            loading={resumingSubscription}
          >
            {t('resumeSubscription')}
          </Button>
        }
      />
    </>
  )
}
